import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ChevronRight } from "lucide-react";
import { useT } from "../i18n";

const FONT_BODY = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro", "Helvetica Neue", Arial, sans-serif',
};
const BORDER = "#E5E7EB";

/**
 * Shared site footer.
 * Optional `crumbs` prop renders a small breadcrumb next to the PropHero logo:
 *   crumbs = [{ label: "Home", to: "/" }, { label: "Areas We Serve" }]
 */
export default function SiteFooter({ crumbs }) {
  const { t } = useT();
  return (
    <footer
      data-testid="site-footer"
      className="border-t"
      style={{
        borderColor: BORDER,
        background: "#FFFFFF",
        ...FONT_BODY,
        letterSpacing: "-0.01em",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
      }}
    >
      <style>{`
        .ph-fx-link {
          position: relative;
          display: inline-block;
        }
        .ph-fx-link::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 1px;
          background: currentColor;
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 300ms cubic-bezier(0.16, 1, 0.3, 1);
          will-change: transform;
          pointer-events: none;
        }
        .ph-fx-link:hover::after { transform: scaleX(1); }
        .ph-fx-fade {
          transition: opacity 500ms cubic-bezier(0.16, 1, 0.3, 1);
          will-change: opacity;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 md:py-8 grid gap-4 lg:gap-8 lg:grid-cols-[minmax(320px,auto)_1fr_auto]">
        {/* Brand + tagline (+ optional breadcrumb) */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/"
              aria-label="PropHero"
              className="inline-flex items-center ph-fx-fade hover:opacity-90"
            >
              <img src="/prophero-logo.png" alt="PropHero" className="h-4 w-auto" />
            </Link>
            {crumbs && crumbs.length > 0 && (
              <nav
                aria-label="Breadcrumb"
                data-testid="footer-breadcrumb"
                className="flex items-center gap-1.5 text-[12px] leading-none text-[#515154]"
              >
                <ChevronRight className="w-3 h-3 text-[#B0B0B5]" strokeWidth={1.75} />
                {crumbs.map((c, i) => {
                  const isLast = i === crumbs.length - 1;
                  return (
                    <React.Fragment key={`${c.label}-${i}`}>
                      <span aria-current={isLast ? "page" : undefined}>{c.label}</span>
                      {!isLast && (
                        <ChevronRight className="w-3 h-3 text-[#B0B0B5]" strokeWidth={1.75} />
                      )}
                    </React.Fragment>
                  );
                })}
              </nav>
            )}
          </div>
          <p className="mt-2 text-[12px] leading-[1.4] text-[#515154] max-w-xs">
            {t("pm.foot.tagline")}
          </p>
        </div>

        {/* Contact */}
        <div className="lg:flex lg:justify-end">
          <div className="lg:min-w-[269px]">
            <div className="text-[12px] font-semibold text-[#1D1D1F]">
              {t("pm.foot.contact")}
            </div>
            <ul className="mt-2 space-y-1.5 text-[12px]">
              <li>
                <a
                  href="mailto:hello@prophero.io"
                  data-testid="footer-email-link"
                  className="group inline-flex items-center gap-2 text-[#515154]"
                >
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="ph-fx-link">
                    hello@prophero.io
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+15613167327"
                  data-testid="footer-phone-link"
                  className="group inline-flex items-center gap-2 text-[#515154]"
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="ph-fx-link">
                    (561) 316-7327
                  </span>
                </a>
              </li>
              <li className="inline-flex items-center gap-2 text-[#515154]">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t("pm.foot.city")}
              </li>
            </ul>
          </div>
        </div>

        {/* Compliance */}
        <div className="lg:justify-self-end">
          <div className="text-[12px] font-semibold text-[#1D1D1F]">
            {t("pm.foot.compliance")}
          </div>
          <p className="mt-2 text-[12px] leading-[1.4] text-[#515154] max-w-xs">
            {t("pm.foot.camLine1")}
            <br />
            {t("pm.foot.camLine2")}
          </p>
        </div>
      </div>

      {/* Bottom bar — two states:
           • wide (lg+): [Copyright] [Links centered] [Location] on one row
           • narrow (<lg): stack — Copyright / Links / Location all on their own rows */}
      <div className="border-t" style={{ borderColor: BORDER }}>
        <div
          className="max-w-7xl mx-auto px-6 lg:px-10 py-3 grid gap-x-8 gap-y-2 text-[12px] text-[#6E6E73]
                     grid-cols-1 [grid-template-areas:'copy'_'links'_'loc']
                     lg:[grid-template-columns:auto_1fr_auto] lg:[grid-template-areas:'copy_links_loc']"
          style={{ letterSpacing: "-0.01em" }}
        >
          <p className="whitespace-nowrap" style={{ gridArea: "copy" }}>
            {t("pm.foot.copyright")}
          </p>
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-self-start lg:justify-self-center"
            style={{ gridArea: "links" }}
          >
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="whitespace-nowrap ph-fx-link"
            >
              {t("pm.foot.privacy")}
            </Link>
            <span aria-hidden className="h-3 w-px bg-[#D2D2D7]" />
            <Link
              to="/areas-we-serve"
              data-testid="footer-areas-link"
              className="whitespace-nowrap ph-fx-link"
            >
              Areas We Serve
            </Link>
            <span aria-hidden className="h-3 w-px bg-[#D2D2D7]" />
            <span className="whitespace-nowrap">{t("pm.foot.tagTech")}</span>
            <span aria-hidden className="h-3 w-px bg-[#D2D2D7]" />
            <span className="whitespace-nowrap">{t("pm.foot.tagCam")}</span>
          </div>
          <p
            className="whitespace-nowrap justify-self-start lg:justify-self-end"
            style={{ gridArea: "loc" }}
          >
            {t("pm.foot.location")}
          </p>
        </div>
      </div>
    </footer>
  );
}
